/**
 * MA-UI-109: Data Enrichment Settings Section
 * 
 * Controls how MissionAssist enriches lead data using ZIP-based intelligence.
 * This is the funnel-level control layer for the enrichment system.
 * 
 * Modes:
 * - Standard: Full enrichment (state, county, timezone, AI summary)
 * - Strict: No derived enrichment (only explicitly collected data)
 * - Minimal: Lightweight enrichment (state, timezone only)
 * 
 * Advanced controls allow per-flag overrides of mode defaults.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Database, 
  ChevronDown, 
  ChevronRight, 
  Info,
  MapPin,
  Clock,
  Building,
  Brain,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// =============================================================================
// Mode Definitions (must match backend enrichment_settings_resolver.py)
// MA-UI-109-FIX-001: Forward compatibility for financial enrichment
// =============================================================================
const MODE_STANDARD = 'standard';
const MODE_STRICT = 'strict';
const MODE_MINIMAL = 'minimal';

// MA-UI-109-FIX-001: Updated to include future financial enrichment flags
const MODE_FLAG_DEFAULTS = {
  [MODE_STANDARD]: {
    enable_zip_state_enrichment: true,
    enable_zip_county_enrichment: true,
    enable_zip_timezone_enrichment: true,
    enable_ai_context_summary: true,
    // Future flags (MA-DATA-001 preparation)
    enable_estimated_home_value: true,
    enable_estimated_income: true,
  },
  [MODE_STRICT]: {
    enable_zip_state_enrichment: false,
    enable_zip_county_enrichment: false,
    enable_zip_timezone_enrichment: false,
    enable_ai_context_summary: false,
    // Future flags
    enable_estimated_home_value: false,
    enable_estimated_income: false,
  },
  [MODE_MINIMAL]: {
    enable_zip_state_enrichment: true,
    enable_zip_county_enrichment: false,
    enable_zip_timezone_enrichment: true,
    enable_ai_context_summary: false,
    // Future flags
    enable_estimated_home_value: false,
    enable_estimated_income: false,
  },
};

const MODE_INFO = {
  [MODE_STANDARD]: {
    label: 'Standard',
    description: 'Recommended. Uses full ZIP-based enrichment.',
    color: 'emerald',
  },
  [MODE_STRICT]: {
    label: 'Strict',
    description: 'Only uses data explicitly collected in the funnel.',
    color: 'amber',
  },
  [MODE_MINIMAL]: {
    label: 'Minimal',
    description: 'Uses essential enrichment like state and time zone only.',
    color: 'blue',
  },
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Resolve effective flags from mode + overrides
 */
function resolveEffectiveFlags(settings) {
  const mode = settings?.enrichment_mode || MODE_STANDARD;
  const modeDefaults = MODE_FLAG_DEFAULTS[mode] || MODE_FLAG_DEFAULTS[MODE_STANDARD];
  
  const flags = {};
  let hasOverrides = false;
  
  for (const key of Object.keys(modeDefaults)) {
    if (settings && typeof settings[key] === 'boolean') {
      flags[key] = settings[key];
      if (settings[key] !== modeDefaults[key]) {
        hasOverrides = true;
      }
    } else {
      flags[key] = modeDefaults[key];
    }
  }
  
  return { flags, hasOverrides, mode };
}

/**
 * Get list of enabled enrichments for display
 * MA-BE-078: Financial enrichments now active (no longer "coming soon")
 */
function getEnabledEnrichments(flags) {
  const enabled = [];
  // Current enrichments
  if (flags.enable_zip_state_enrichment) enabled.push('State');
  if (flags.enable_zip_county_enrichment) enabled.push('County');
  if (flags.enable_zip_timezone_enrichment) enabled.push('Time Zone');
  // MA-BE-078: Financial enrichments now active
  if (flags.enable_estimated_home_value) enabled.push('Est. Home Value');
  if (flags.enable_estimated_income) enabled.push('Est. Income');
  if (flags.enable_ai_context_summary) enabled.push('AI Summary');
  return enabled;
}

/**
 * Get list of all enrichment items for detailed display
 * MA-BE-078: Financial enrichments now active (no longer "coming soon")
 */
function getAllEnrichmentItems(flags) {
  return [
    { key: 'state', label: 'State', enabled: flags.enable_zip_state_enrichment, comingSoon: false },
    { key: 'county', label: 'County', enabled: flags.enable_zip_county_enrichment, comingSoon: false },
    { key: 'timezone', label: 'Time Zone', enabled: flags.enable_zip_timezone_enrichment, comingSoon: false },
    { key: 'home_value', label: 'Est. Home Value Range', enabled: flags.enable_estimated_home_value, comingSoon: false },
    { key: 'income', label: 'Est. Income Range', enabled: flags.enable_estimated_income, comingSoon: false },
    { key: 'ai_summary', label: 'AI Context Summary', enabled: flags.enable_ai_context_summary, comingSoon: false },
  ];
}

// =============================================================================
// Mode Selector Component
// =============================================================================
function ModeSelector({ currentMode, onModeChange }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {[MODE_STANDARD, MODE_STRICT, MODE_MINIMAL].map((mode) => {
        const info = MODE_INFO[mode];
        const isActive = currentMode === mode;
        
        return (
          <button
            key={mode}
            onClick={() => onModeChange(mode)}
            className={cn(
              "relative flex flex-col items-start p-4 rounded-lg border-2 transition-all text-left",
              isActive
                ? `border-${info.color}-500 bg-${info.color}-500/10`
                : "border-white/10 bg-white/[0.02] hover:border-white/20"
            )}
            data-testid={`enrichment-mode-${mode}`}
          >
            {isActive && (
              <div className={`absolute top-2 right-2 w-2 h-2 rounded-full bg-${info.color}-400`} />
            )}
            <span className={cn(
              "text-sm font-semibold mb-1",
              isActive ? `text-${info.color}-300` : "text-slate-200"
            )}>
              {info.label}
            </span>
            <span className="text-xs text-slate-400 leading-relaxed">
              {info.description}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// =============================================================================
// Advanced Controls Component
// =============================================================================
function AdvancedControls({ settings, onSettingsChange, modeDefaults }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const handleToggle = (key, value) => {
    onSettingsChange({
      ...settings,
      [key]: value,
    });
  };
  
  const controls = [
    {
      key: 'enable_zip_state_enrichment',
      label: 'State Enrichment',
      description: 'Derive state from ZIP code',
      icon: MapPin,
    },
    {
      key: 'enable_zip_county_enrichment',
      label: 'County Enrichment',
      description: 'Derive county from ZIP code',
      icon: Building,
    },
    {
      key: 'enable_zip_timezone_enrichment',
      label: 'Timezone Enrichment',
      description: 'Derive timezone from ZIP code',
      icon: Clock,
    },
    {
      key: 'enable_ai_context_summary',
      label: 'AI Context Summary',
      description: 'Generate contextual summary for AI agents',
      icon: Brain,
    },
  ];
  
  // Check if any value differs from mode default
  const hasOverrides = controls.some(
    (c) => typeof settings?.[c.key] === 'boolean' && settings[c.key] !== modeDefaults[c.key]
  );
  
  return (
    <div className="space-y-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors"
        data-testid="advanced-controls-toggle"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        <span>Advanced Enrichment Controls</span>
        {hasOverrides && (
          <Badge 
            variant="outline" 
            className="ml-2 text-[10px] border-amber-500/30 text-amber-400 bg-amber-500/10"
          >
            Custom overrides active
          </Badge>
        )}
      </button>
      
      {isExpanded && (
        <Card className="border-white/10 bg-white/[0.02]">
          <CardContent className="p-4 space-y-4">
            {controls.map((control) => {
              const Icon = control.icon;
              const currentValue = typeof settings?.[control.key] === 'boolean' 
                ? settings[control.key] 
                : modeDefaults[control.key];
              const isOverridden = typeof settings?.[control.key] === 'boolean' && 
                settings[control.key] !== modeDefaults[control.key];
              
              return (
                <div 
                  key={control.key}
                  className={cn(
                    "flex items-center justify-between p-3 rounded-lg transition-colors",
                    isOverridden ? "bg-amber-500/5" : "bg-white/[0.02]"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center",
                      currentValue ? "bg-indigo-500/20 text-indigo-300" : "bg-slate-500/20 text-slate-400"
                    )}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-200">
                        {control.label}
                        {isOverridden && (
                          <span className="ml-2 text-[10px] text-amber-400">(overridden)</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500">{control.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={currentValue}
                    onCheckedChange={(value) => handleToggle(control.key, value)}
                    data-testid={`enrichment-toggle-${control.key}`}
                  />
                </div>
              );
            })}
            
            {/* Reset to mode defaults button */}
            {hasOverrides && (
              <button
                onClick={() => {
                  // Remove all override keys to reset to mode defaults
                  const { 
                    enable_zip_state_enrichment, 
                    enable_zip_county_enrichment, 
                    enable_zip_timezone_enrichment, 
                    enable_ai_context_summary,
                    ...rest 
                  } = settings || {};
                  onSettingsChange(rest);
                }}
                className="w-full text-center text-xs text-slate-400 hover:text-slate-200 py-2 transition-colors"
                data-testid="reset-overrides-button"
              >
                Reset to mode defaults
              </button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// Enrichment Summary Component
// MA-BE-078: Financial enrichments now active (no longer "coming soon")
// =============================================================================
function EnrichmentSummary({ effectiveFlags }) {
  const allItems = getAllEnrichmentItems(effectiveFlags.flags);
  const currentEnabled = getEnabledEnrichments(effectiveFlags.flags);
  
  if (currentEnabled.length === 0) {
    return (
      <Card className="border-white/5 bg-white/[0.01]">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-amber-300">No ZIP-based enrichment enabled</p>
              <p className="text-xs text-slate-500 mt-1">
                Lead data will not be enriched from ZIP codes. Fields will remain as provided.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="border-emerald-500/10 bg-emerald-500/5">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <Sparkles className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-emerald-300 mb-2">
              ZIP Intelligence Includes
            </p>
            <div className="flex flex-wrap gap-2">
              {allItems.map((item) => (
                <span
                  key={item.key}
                  className={cn(
                    "inline-flex items-center gap-1 px-2 py-1 rounded text-xs",
                    item.enabled && !item.comingSoon
                      ? "bg-emerald-500/20 text-emerald-300"
                      : item.comingSoon
                        ? "bg-slate-500/10 text-slate-500"
                        : "bg-slate-500/10 text-slate-500 line-through"
                  )}
                >
                  {item.label}
                  {item.comingSoon && (
                    <span className="text-[10px] text-slate-600">(soon)</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-500 pl-7">
          Enabled enrichments will be applied automatically when a ZIP code is captured.
        </p>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Main Data Enrichment Section Component
// =============================================================================
export default function DataEnrichmentSection({ settings, onSettingsChange }) {
  // Get enrichment settings from funnel settings
  const enrichmentSettings = settings?.enrichment || {};
  const currentMode = enrichmentSettings.enrichment_mode || MODE_STANDARD;
  
  // Compute effective flags
  const effectiveFlags = useMemo(
    () => resolveEffectiveFlags(enrichmentSettings),
    [enrichmentSettings]
  );
  
  // Get mode defaults for comparison
  const modeDefaults = MODE_FLAG_DEFAULTS[currentMode] || MODE_FLAG_DEFAULTS[MODE_STANDARD];
  
  // Handle mode change
  const handleModeChange = (newMode) => {
    // When changing mode, clear any overrides to use new mode defaults
    onSettingsChange({
      ...settings,
      enrichment: {
        enrichment_mode: newMode,
        // Clear override flags so mode defaults take effect
      },
    });
  };
  
  // Handle individual flag changes
  const handleFlagChange = (updatedEnrichmentSettings) => {
    onSettingsChange({
      ...settings,
      enrichment: {
        ...enrichmentSettings,
        ...updatedEnrichmentSettings,
      },
    });
  };
  
  return (
    <div className="space-y-6" data-testid="data-enrichment-section">
      {/* Section Header */}
      <div>
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-indigo-400" />
          <h2 className="text-xl font-bold text-slate-100">Data Enrichment</h2>
        </div>
        <p className="text-slate-200/60 text-sm mt-1">
          Control how MissionAssist enriches lead data using ZIP-based intelligence and AI-generated context.
        </p>
      </div>
      
      {/* ZIP Dependency Note - MA-UI-109-FIX-001: Improved messaging */}
      <Card className="border-white/5 bg-white/[0.01]">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-xs text-slate-300">
                ZIP-based enrichment uses values collected from <strong className="text-slate-100">Zip Code</strong> inputs in this funnel.
              </p>
              <p className="text-xs text-slate-400">
                Add a Zip Code input type to enable geographic and demographic enrichment.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Mode Selector Card */}
      <Card className="border-white/10 bg-white/[0.02]">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium text-slate-300">
            Enrichment Mode
          </CardTitle>
          <CardDescription className="text-xs text-slate-500">
            Choose how MissionAssist enriches lead data from ZIP codes
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0 space-y-5">
          <ModeSelector 
            currentMode={currentMode} 
            onModeChange={handleModeChange} 
          />
          
          {/* Advanced Controls */}
          <AdvancedControls
            settings={enrichmentSettings}
            onSettingsChange={handleFlagChange}
            modeDefaults={modeDefaults}
          />
        </CardContent>
      </Card>
      
      {/* Enrichment Summary */}
      <EnrichmentSummary effectiveFlags={effectiveFlags} />
    </div>
  );
}
